import { identity, isEmpty, pickBy } from 'lodash-es';
import Cookies from 'universal-cookie';
import { v4 } from 'uuid';

import { isEnvironmentLocalAndDevelopment } from '../../Environment';
import { loggerUtils } from '../../Logging';
import type { IIdentifyUserProps, TEventProperties } from '../types';
import { EventType } from '../types';

// Override the user's current IP address for every API call
export const ANONYMOUS_IP_ADDRESS = '0.0.0.0';

const cookies = new Cookies();

const internalState = {
  sessionStart: -1,
  sessionDeviceId: '',
};

const getDeviceId = (): string => {
  const getDeviceIdFromLocalStorage = (localStorage: Storage): string => {
    const existingDeviceId = localStorage.getItem('amplitudeDeviceId');
    if (existingDeviceId) {
      return existingDeviceId;
    }
    const newDeviceId = String(v4());
    localStorage.setItem('amplitudeDeviceId', newDeviceId);
    return newDeviceId;
  };

  const getDeviceIdFromMemory = (): string => {
    if (!internalState.sessionDeviceId) {
      internalState.sessionDeviceId = String(v4());
    }
    return internalState.sessionDeviceId;
  };

  let localStorage;
  try {
    localStorage = window.localStorage;
  } catch {
    // If localStorage is not available, just create and/or use the device ID in memory
  }

  if (localStorage) {
    return getDeviceIdFromLocalStorage(localStorage);
  }
  return getDeviceIdFromMemory();
};

export const getSegmentSessionId = (): number => {
  if (internalState.sessionStart < 0) {
    internalState.sessionStart = Math.round(Date.now());
  }
  return internalState.sessionStart;
};

const getMarketingCookies = (): Record<string, string> | undefined => {
  const data = pickBy(
    {
      source: cookies.get('utm_source'),
      medium: cookies.get('utm_medium'),
      term: cookies.get('utm_term'),
      content: cookies.get('utm_content'),
      name: cookies.get('utm_campaign'),
    },
    identity
  );
  return isEmpty(data) ? undefined : data;
};

/**
 * Wrapper for Segment's user().traits() method, which returns the Segment user's
 * persistent traits from localStorage.
 */
export const getSegmentPersistentTraits = (): Record<string, any> | undefined => {
  if (typeof window.analytics === 'undefined') {
    return undefined;
  }
  // The following checks are here because, for some reason, window.analytics.user()
  // isn't available in Cypress tests.
  if (typeof window.analytics.user !== 'function') {
    return undefined;
  }
  const user = window.analytics.user();
  if (typeof user.traits !== 'function') {
    return undefined;
  }
  return user.traits();
};

/**
 * Gets the user's Braze ID. This is a unique identifier for the user that is added to
 * email links by Braze as the bid query parameter. This function attempts to find the
 * ID either in the bid query parameter or in the Segment user's persistent traits.
 */
const getBrazeId = (): string | undefined => {
  const urlParams = new URLSearchParams(window.location.search);
  const brazeId = urlParams.get('bid');
  if (brazeId) {
    return brazeId;
  }
  const traits = getSegmentPersistentTraits();
  if (!traits) {
    return undefined;
  }
  return traits.braze_id;
};

const getTraitsForContext = (): Record<string, any> | undefined => {
  const brazeId = getBrazeId();
  if (brazeId) {
    return {
      braze_id: brazeId,
    };
  }
  return undefined;
};

const getSegmentContext = (): Record<string, any> | undefined => {
  return pickBy(
    {
      campaign: getMarketingCookies(),
      ip: ANONYMOUS_IP_ADDRESS,
      device: {
        id: getDeviceId(),
      },
      traits: getTraitsForContext(),
    },
    identity
  );
};

/**
 * Makes a request to Segment, if available.
 *
 * TODO: Define a TS type over the Segment API.
 * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/
 */
// tslint:disable-next-line no-any
const sendCommand = (command: EventType, ...args: any[]): void => {
  if (isEnvironmentLocalAndDevelopment()) {
    loggerUtils.logMessageToConsole('segment:', command, ...args);
  }
  if (typeof window.analytics !== 'undefined') {
    window.analytics[command](...args);
  }
};

/**
 * Returns the properties that should be sent to Segment.
 *
 * <p>Augments the given properties so Segment has enough information.
 * @param properties Properties that callers want to send to Segment
 */
const getSegmentProperties = (
  properties?: TEventProperties
): TEventProperties & Record<string, number> => {
  return {
    ...properties,
    color_session_id: getSegmentSessionId(),
  };
};

/**
 * Identifies the user based on their patient ID.
 *
 * <p>The user is identified by their patient ID because it's not considered PHI and can be used to
 * correlate tracking events back to the user in the internal data warehouse.
 * @param props Patient properties, including information to set Segment traits.
 * @see https://getcolor.atlassian.net/browse/DATAPLAT-347
 * @see https://docs.google.com/document/d/1v1r8ImmFupQX0sj0DC2oRq-qr859CUw01AvT0a-kveU
 * @see https://segment.com/docs/connections/spec/identify/
 */
export const identifySegmentUser = (props: IIdentifyUserProps): void => {
  const { isColorEmployee = false, patientId, properties } = props;

  const brazeId = getBrazeId();
  const segmentProperties = {
    ...getSegmentProperties(properties),
    ...(brazeId ? { braze_id: brazeId } : {}),
  };

  sendCommand(
    /* command */ EventType.IDENTIFY,
    /* userId */ patientId,
    /* traits */ {
      is_color_employee: isColorEmployee,
      ...segmentProperties,
    },
    /* options */ { context: getSegmentContext() }
  );
};

/**
 * Resets the id, including anonymousId, and clears traits for the currently identified user and
 * group.
 */
export const resetSegmentUser = (): void => {
  sendCommand(EventType.RESET, {});
  internalState.sessionStart = -1;
  internalState.sessionDeviceId = '';
};

/**
 * Logs a page view to Segment.
 * @param name An optional name for the page
 * @param properties An optional set of properties to include with this event
 */
export const logSegmentPageView = (name?: string, properties?: TEventProperties): void => {
  sendCommand(
    /* command */ EventType.PAGE,
    /* category */ undefined,
    /* name */ name,
    /* properties */ getSegmentProperties(properties),
    /* options */ {
      context: getSegmentContext(),
    }
  );
};

/**
 * Logs an event to Segment.
 * @param event The event name
 * @param properties An optional set of properties to include with this event,
 *   describing perhaps the user who performed the event, or the event itself
 */
export const trackSegmentEvent = (event: string, properties: TEventProperties = {}): void => {
  sendCommand(
    /* command */ EventType.TRACK,
    /* event */ event,
    /* properties */ getSegmentProperties(properties),
    /* options */ {
      context: getSegmentContext(),
    }
  );
};
